/** @type {Map<string, (el: HTMLElement) => void>} */
const mounts = new Map();

/** @type {Map<string, (el: HTMLElement) => void>} */
const unmounts = new Map();

/** @type {MutationObserver | null} */
let observer = null;

/** @type {WeakMap<HTMLElement, Map<Function, object>>} */
const instances = new WeakMap();

/** @type {WeakMap<HTMLElement, Set<Function>>} tracks which mount callbacks already ran on an element */
const mountedCallbacks = new WeakMap();

/**
 * Returns the component instance attached to `el`, mounting it on demand if it
 * isn't mounted yet — so access order between components is irrelevant. If
 * multiple components are mounted on the same element, pass the factory or class
 * (the value given to mount/mountNew) as the second argument.
 *
 * @template T
 * @param {HTMLElement} el
 * @param {new (...args: any[]) => T} [ComponentClass]
 * @returns {T | undefined}
 */
export function componentOf(el, ComponentClass) {
    if (!instances.get(el)) mounts.forEach((cb, sel) => el.matches(sel) && cb(el));
    const map = instances.get(el);
    if (!map) return undefined;
    if (ComponentClass) return map.get(ComponentClass);
    if (map.size === 1) return map.values().next().value;
    throw new Error('Multiple components on element — pass the factory or class to componentOf()');
}

/**
 * Registers a component instance under `el`, keyed by `key` (a callback or class).
 * @private
 */
function register(el, key, instance) {
    let map = instances.get(el);
    if (!map) instances.set(el, map = new Map());
    map.set(key, instance);
}

/**
 * Calls unmount() on every component registered under `el` and drops the registry entry.
 * @private
 */
function destroyInstances(el) {
    mountedCallbacks.delete(el);
    const map = instances.get(el);
    if (!map) return;
    map.forEach(instance => instance.unmount?.());
    instances.delete(el);
}

/**
 * Run callback for all existing and future elements matching selector.
 * When called without a callback, returns a deferred proxy — all method calls
 * are recorded and replayed on each matched element on mount.
 *
 * @param {string} selector - CSS selector to match elements
 * @param {(this: HTMLElement, el: HTMLElement, refs: Refs, emit: Emit) => void} [callback] - Called for each mount
 * @returns {HTMLElement}
 */
export function mount(selector, callback) {
    if (!callback) return deferredProxy(cb => mount(selector, cb));

    const wrapped = el => {
        let ran = mountedCallbacks.get(el);
        if (ran?.has(callback)) return;
        if (!ran) mountedCallbacks.set(el, ran = new Set());
        ran.add(callback);
        const instance = callback.call(el, el, buildRefs(el), buildEmit(el));
        if (instance) register(el, callback, instance);
    };
    mounts.set(selector, wrapped);
    unmount(selector, destroyInstances);
    initObserver();
    // Defer the initial sweep so every mount() call registers in `mounts` first; the
    // callbacks then run one microtask later, letting componentOf() mount any component
    // on demand regardless of import order. wrapped is idempotent per (element, callback),
    // so the sweep and on-demand mounts never double-initialize the same element.
    queueMicrotask(() => document.querySelectorAll(selector).forEach(wrapped));
}

/**
 * Run callback whenever an element matching selector is removed from DOM.
 * When called without a callback, returns a deferred proxy — all method calls
 * are recorded and replayed on each matched element on unmount.
 *
 * @param {string} selector - CSS selector to match elements
 * @param {(this: HTMLElement, el: HTMLElement, refs: Refs) => void} [callback] - Called for each unmount
 * @returns {HTMLElement}
 */
export function unmount(selector, callback) {
    if (!callback) return deferredProxy(cb => unmount(selector, cb));

    unmounts.set(selector, el => callback.call(el, el, buildRefs(el)));
    initObserver();
}

/**
 * Like mount(), but constructs the component with `new Component(el, refs)` instead
 * of calling it as a factory — so `this` inside is the instance. Accepts anything
 * constructable: a class or a regular function (NOT an arrow). The instance is
 * registered (retrievable via componentOf(el[, Component])); its `unmount()` method,
 * if present, runs when the element leaves the DOM.
 *
 * @param {string} selector - CSS selector to match elements
 * @param {new (el: HTMLElement, refs: Refs, emit: Emit) => object} Component - Class or constructor function (not an arrow)
 */
export function mountNew(selector, Component) {
    mount(selector, (el, refs, emit) => {
        register(el, Component, new Component(el, refs, emit));
    });
}

/**
 * @typedef {Object<string, HTMLElement> & {all: Object<string, NodeListOf<HTMLElement>>}} Refs
 */

/**
 * Builds a refs proxy scoped to `el`. Property names are converted to kebab-case
 * and matched with `~=` so a single `data-ref` may hold several space-separated ids:
 *   refs.fooBar   → el.querySelector('[data-ref~="foo-bar"]')
 *   refs.all.page → el.querySelectorAll('[data-ref~="page"]')
 *
 * @private
 * @param {HTMLElement} el
 * @returns {Refs}
 */
function buildRefs(el) {
    const kebab = name => name.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`);
    const all = new Proxy({}, {
        get: (_, name) => typeof name === 'string'
            ? el.querySelectorAll(`[data-ref~="${kebab(name)}"]`)
            : undefined,
    });
    return new Proxy({}, {
        get: (_, name) => {
            if (name === 'all') return all;
            return typeof name === 'string' ? el.querySelector(`[data-ref~="${kebab(name)}"]`) : undefined;
        },
    });
}

/**
 * @typedef {(name: string, detail?: any, options?: CustomEventInit) => boolean} Emit
 */

/**
 * Builds an emit helper that dispatches a bubbling CustomEvent from `el`.
 * Bubbling events without a namespace can collide with listeners higher up the
 * tree, so in dev we warn when a name has no `:` prefix and bubbling wasn't set
 * explicitly — nudging callers to either namespace the event or opt out.
 *
 * @private
 * @param {HTMLElement} el
 * @returns {Emit}
 */
function buildEmit(el) {
    return (name, detail, options) => {
        if (process.env.NODE_ENV !== 'production' && !name.includes(':') && options?.bubbles === undefined) {
            console.warn(`emit("${name}"): bubbling event without a namespace may collide — use "prefix:name" or pass an explicit { bubbles }.`);
        }
        return el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail, ...options }));
    };
}

/**
 * Returns a deferred jQuery-like proxy — all method calls are recorded
 * and replayed on $(el) when the element is mounted.
 *
 * @param {string} selector - CSS selector to match elements
 * @returns {JQuery}
 */
export function $mount(selector) {
    return deferredProxy(cb => mount(selector, cb), el => $(el));
}

/**
 * Builds a proxy that records every method call and replays them on each
 * element matched by `register`. `wrap` lets callers re-target the replay
 * (e.g. through jQuery) — defaults to identity for native DOM.
 *
 * @private
 * @param {(callback: (el: HTMLElement) => void) => void} register
 * @param {(el: HTMLElement) => any} [wrap]
 */
function deferredProxy(register, wrap = el => el) {
    const calls = [];
    const proxy = new Proxy({}, {
        get: (_, method) => (...args) => {
            calls.push([method, args]);
            return proxy;
        },
    });
    queueMicrotask(() => {
        register(el => {
            calls.forEach(([method, args]) => wrap(el)[method](...args));
        });
    });
    return proxy;
}

/**
 * Initializes the MutationObserver (only once).
 * @private
 */
function initObserver() {
    if (observer) return;

    observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
            for (const node of m.addedNodes) {
                if (!(node instanceof HTMLElement)) continue;
                mounts.forEach((cb, sel) => {
                    if (node.matches(sel)) cb.call(node, node);
                    node.querySelectorAll(sel).forEach((el) => cb.call(el, el));
                });
            }
            for (const node of m.removedNodes) {
                if (!(node instanceof HTMLElement)) continue;
                unmounts.forEach((cb, sel) => {
                    if (node.matches(sel)) cb.call(node, node);
                    node.querySelectorAll(sel).forEach((el) => cb.call(el, el));
                });
            }
        }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
}